import { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryDown } from 'components/utils/breakpoint'
import variables from 'styles/vendor/bootstrap/variables'
import BodyText from 'LuxKit/Typography/BodyText'
import { replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import config from 'constants/config'
import AssistChip from 'LuxKit/Chips/AssistChip'
import { destinationsMap, formatExperienceDestinationLabel } from 'constants/experience'
import { trackEvent } from 'analytics/analytics'
import { useIntl } from 'react-intl'
import { searchEventWithContext } from 'analytics/snowplow/events'

import uuidV4 from 'lib/string/uuidV4Utils'

const Wrapper = styled.div`
  margin-top: ${rem(16)};
  align-self: flex-start;
  position: relative;
`

const LabelsWrapper = styled.nav`
  margin-top: ${rem(8)};
  -webkit-overflow-scrolling: touch;
  padding-inline-end: ${rem(variables.gutterWidth / 2)};
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;

  > * + * {
    margin-inline-start: ${rem(8)};
  }

  ${mediaQueryDown.tablet} {
    display: flex;
    flex-wrap: wrap;
    gap: ${rem(8)};

    > * + * {
      margin-inline-start: 0;
    }
  }
`

interface Props {
  className?: string
  weight?: 'normal' | 'bold'
  colour?: 'neutral-eight' | 'neutral-two' | 'dark-contrast' | 'light-foreground-alt'
  variant?: 'small' | 'medium' | 'large'
  onClick?: () => void
}

function ExperiencePageTrendingDestinations({
  className,
  colour = 'dark-contrast',
  weight = 'bold',
  variant = 'medium',
  onClick,
}: Props) {
  const intl = useIntl()
  const destinations = destinationsMap(config.BRAND)

  const handleClick = useCallback((canonicalName: string) => {
    trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: {
        destinationIds: [],
        destinationType: 'location',
        destinations: [canonicalName],
        vertical: 'experiences',
        searchId: uuidV4(),
      },
    }))
    onClick?.()
  }, [onClick])

  return (
    <Wrapper className={className}>
      <BodyText colour={colour} variant={variant} weight={weight}>
        {intl.formatMessage({
          id: 'Ka5iXPYkcYMOt0-Gk0pf',
          defaultMessage: 'Popular searches',
          description: 'Label for trending destination search chips on experiences landing page',
        })}
      </BodyText>
      <LabelsWrapper>
        {destinations.map(destination => {
          const query = replaceExperienceQueryString({ destinationId: destination.id, destinationName: destination.name })

          return <AssistChip
            size="medium"
            variant="filled"
            key={destination.id}
            to={`/search/experiences?${query}`}
            onClick={() => handleClick(destination.name)}
          >
            {formatExperienceDestinationLabel(intl, destination.name) ?? destination.name}
          </AssistChip>
        })}
      </LabelsWrapper>
    </Wrapper>
  )
}

export default ExperiencePageTrendingDestinations
